"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Zap, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import PlanCardView from "../components/PlanCardView";
import PlanTableView from "../components/PlanTableView";
import type { LucideIcon } from "lucide-react";

interface Plan {
  _id: string;
  planName: string;
  noOfCompany: number;
  planType: string;
  duration: number;
  amount: number;
  createdDate: string;
}

export default function PlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [isTableView, setIsTableView] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get("/plans");
      const sortedPlans = response.data.plans
        .map((plan: Plan) => ({
          ...plan,
          createdDate: new Date(plan.createdDate).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }),
        }))
        .sort(
          (a: Plan, b: Plan) =>
            new Date(a.createdDate).getTime() -
            new Date(b.createdDate).getTime()
        );

      console.log("Fetched plans:", sortedPlans);
      setPlans(sortedPlans);
    } catch (error) {
      toast.error("Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (planId: string) => {
    setPurchasing(planId);
    try {
      // Show immediate feedback
      const selectedPlan = plans.find((plan) => plan._id === planId);
      if (selectedPlan) {
        toast.success(`Redirecting to order ${selectedPlan.planName} plan...`);
      }

      // Navigate to order page with selected plan
      navigate(`/orders?planId=${planId}`);
    } catch (error) {
      toast.error("Failed to navigate to order page");
      setPurchasing(null);
    }
    // Note: Don't clear purchasing state here as user is navigating away
  };

  const getPlanIcon = (planType: string): LucideIcon => {
    switch (planType.toLowerCase()) {
      case "single":
        return Shield;
      case "multi":
        return Zap;
      default:
        return Star;
    }
  };

  const getPlanColor = (index: number) => {
    const colors = [
      "from-blue-500 to-blue-600",
      "from-purple-500 to-purple-600",
      "from-green-500 to-green-600",
      "from-orange-500 to-orange-600",
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-3xl font-bold text-white mb-2">Choose Your Plan</h1>
        <p className="text-white/80">
          Select the perfect plan for your business needs.
        </p>
      </motion.div>

      <div className="flex justify-end mb-4">
        <Button
          onClick={() => setIsTableView(!isTableView)}
          variant="outline"
          className="bg-blue-600 text-white hover:bg-blue-700 border-blue-700"
        >
          Switch to {isTableView ? "Card View" : "Table View"}
        </Button>
      </div>

      {plans.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <p className="text-white/70 text-lg">
            No plans available at the moment.
          </p>
        </motion.div>
      ) : isTableView ? (
        <PlanTableView
          plans={plans}
          purchasing={purchasing}
          handlePurchase={handlePurchase}
          getPlanIcon={getPlanIcon}
          getPlanColor={getPlanColor}
        />
      ) : (
        <PlanCardView
          plans={plans}
          purchasing={purchasing}
          handlePurchase={handlePurchase}
          getPlanIcon={getPlanIcon}
          getPlanColor={getPlanColor}
        />
      )}
    </div>
  );
}
